import React, { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, Place } from "@mui/icons-material";
import ContactInfoForm, { ContactInfoData } from "./ContactInfoForm";
import {
  useAdminAccounts,
  useAdminBusinesses,
  useAdminLocations,
} from "../../../stores/Admin/locations/locations.queries";
import {
  useCreateLocation,
  useDeleteLocation,
  useUpdateLocation,
} from "../../../stores/Admin/locations/locations.mutations";
import type { CreateLocationDto, UpdateLocationDto } from "../../../stores/Admin/locations/locations.mutations";

interface LocationFormData {
  id: string;
  name: string;
  fullName: string;
  businessId: string;
  primaryAccountId: string;
  accountIds: string[];
  addressContactInfo: ContactInfoData;
}

const emptyForm: LocationFormData = {
  id: "",
  name: "",
  fullName: "",
  businessId: "",
  primaryAccountId: "",
  accountIds: [],
  addressContactInfo: {},
};

const LocationSection: React.FC = () => {
  const { data: locations = [], isLoading, isError } = useAdminLocations();
  const { data: accounts = [] } = useAdminAccounts();
  const { data: businesses = [] } = useAdminBusinesses();
  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();
  const deleteMutation = useDeleteLocation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<LocationFormData>(emptyForm);

  const isEditMode = editId !== null;

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row: any) => {
    setEditId(row.id);
    setForm({
      id: row.id,
      name: row.name || "",
      fullName: row.fullName || "",
      businessId: row.businessId || "",
      primaryAccountId: row.primaryAccount?.id || "",
      accountIds: row.accounts?.map((a: any) => a.id) || [],
      addressContactInfo: row.address?.contactInfo || {},
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: form.name,
        fullName: form.fullName || undefined,
        businessId: form.businessId || undefined,
        primaryAccountId: form.primaryAccountId || undefined,
        accountIds: form.accountIds,
        addressContactInfo: form.addressContactInfo,
      };
      if (isEditMode) {
        await updateMutation.mutateAsync({ id: editId!, dto: payload as UpdateLocationDto });
      } else {
        await createMutation.mutateAsync({ ...payload, id: form.id, name: form.name } as CreateLocationDto);
      }
      setDialogOpen(false);
    } catch {
      // error handled by mutation onError
    }
  };

  const handleDelete = (row: any) => {
    if (!window.confirm(`Standort "${row.name}" wirklich löschen?`)) return;
    deleteMutation.mutate(row.id);
  };

  const getAddressString = (addr: any) => {
    if (!addr?.contactInfo) return "-";
    const ci = addr.contactInfo;
    const parts = [ci.addressLine1, ci.zip, ci.city].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "-";
  };

  const cellSx = { fontWeight: "bold", backgroundColor: "#f5f5f5" };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Place sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6">Standorte</Typography>
            <Typography variant="body2" color="text.secondary">
              {locations.length} Einträge
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={openCreate}
        >
          Neuer Standort
        </Button>
      </Box>

      {/* Table */}
      <Paper>
        <TableContainer sx={{ maxHeight: "calc(100vh - 380px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={cellSx}>Name</TableCell>
                <TableCell sx={cellSx}>Unternehmen</TableCell>
                <TableCell sx={cellSx}>Hauptmandant</TableCell>
                <TableCell sx={cellSx}>Mandanten</TableCell>
                <TableCell sx={cellSx}>Adresse</TableCell>
                <TableCell sx={cellSx} align="center">
                  Aktionen
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Alert severity="error">Standorte konnten nicht geladen werden</Alert>
                  </TableCell>
                </TableRow>
              ) : locations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Keine Standorte vorhanden
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                locations.map((row: any) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => openEdit(row)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.name}
                      </Typography>
                      {row.fullName && row.fullName !== row.name && (
                        <Typography variant="caption" color="text.secondary">
                          {row.fullName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.businessName ? (
                        <Chip
                          label={row.businessName}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {row.primaryAccount ? (
                        <Chip
                          label={row.primaryAccount.name}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {row.accounts?.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {row.accounts.slice(0, 2).map((a: any) => (
                            <Chip
                              key={a.id}
                              label={a.name}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {row.accounts.length > 2 && (
                            <Chip
                              label={`+${row.accounts.length - 2}`}
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                        {getAddressString(row.address)}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Bearbeiten">
                        <IconButton
                          size="small"
                          onClick={() => openEdit(row)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(row)}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#fff3e0" }}>
          {isEditMode
            ? `Standort bearbeiten: ${editId}`
            : "Neuen Standort erstellen"}
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <Grid container spacing={2}>
            {!isEditMode && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="ID"
                  required
                  value={form.id}
                  onChange={(e) => setForm({ ...form, id: e.target.value })}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Voller Name"
                value={form.fullName}
                onChange={(e) =>
                  setForm({ ...form, fullName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={businesses}
                getOptionLabel={(o: any) => o.name || o.id || ""}
                value={
                  businesses.find((b: any) => b.id === form.businessId) || null
                }
                onChange={(_, v: any) =>
                  setForm({ ...form, businessId: v?.id || "" })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Unternehmen" />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={accounts}
                getOptionLabel={(o: any) => o.name || o.id || ""}
                value={
                  accounts.find(
                    (a: any) => a.id === form.primaryAccountId
                  ) || null
                }
                onChange={(_, v: any) =>
                  setForm({ ...form, primaryAccountId: v?.id || "" })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Hauptmandant" />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                size="small"
                options={accounts}
                getOptionLabel={(o: any) => o.name || o.id || ""}
                value={accounts.filter((a: any) =>
                  form.accountIds.includes(a.id)
                )}
                onChange={(_, v: any[]) =>
                  setForm({ ...form, accountIds: v.map((x) => x.id) })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Zugeordnete Mandanten" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option: any, index: number) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
              />
            </Grid>
          </Grid>

          <Typography
            variant="subtitle1"
            sx={{ mt: 2, mb: 1, fontWeight: 600 }}
          >
            Standort-Adresse
          </Typography>
          <ContactInfoForm
            value={form.addressContactInfo}
            onChange={(ci) => setForm({ ...form, addressContactInfo: ci })}
            defaultExpanded
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.name || (!isEditMode && !form.id)}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationSection;
